import '@fontsource-variable/inter';
import './styles/base.css';
import './styles/home.css';
import './styles/agenda.css';
import './styles/prejoin.css';
import './styles/meeting.css';
import './styles/panels.css';
import './styles/pip.css';

import { el, clear } from './ui/dom.js';
import { icon } from './ui/icons.js';
import { toast } from './ui/toast.js';
import { initTheme } from './ui/theme.js';
import { mountHome } from './ui/home.js';
import { brandLockup } from './ui/brand.js';
import { parseHash, roomFromPath } from './lib/codes.js';
import { isRoomCode, normalizeRoomCode, RoomLease, RoomWarmer, endRoom, hostFields } from './lib/rooms.js';
import { RoomSession } from './net/session.js';
import { LinkHealth } from './lib/link-health.js';
import { getPref, clearMeetingState } from './lib/prefs.js';
import { initAuth, getUser, isSignedIn, takeAuthError } from './lib/auth.js';
import { startSessionWatch } from './lib/session-watch.js';
import { silenceClosedPeerConnectionNoise } from './lib/webrtc-noise.js';
import { createCrm } from './lib/crm.js';
import { applyLanguage, detectLanguage } from './lib/i18n.js';

initTheme();
// Room type, lock and passcode are asked for at every start and never kept:
// wipe anything an earlier build left behind.
clearMeetingState();
// Quiet the one benign uncaught rejection the peer layer throws when a
// connection is torn down mid-handshake (the host self-probe does, every 15 s).
silenceClosedPeerConnectionNoise();

const root = document.getElementById('app');

/** @type {{media, session, roomCode, lease, warmer, linkHealth, disposers}|null} the active call */
let activeCall = null;
/** @type {{callEnded: () => void}|null} */
let sessionWatch = null;
/** @type {(() => void)|null} cleanup for the mounted view */
let unmount = null;
/** Why the person is signed out, to say on the sign-in page (from the CRM's answer). Shown once. */
let authNotice = null;
/** In-memory name (the signed-in host's name, when we have nothing else). */
let userName = null;

const ctx = {
  // The signed-in member's own CRM calendar, for the home screen's list of meetings (lib/crm.js,
  // read through the backend with their CRM token). Inert for a visitor.
  calendar: createCrm({ refreshToken: () => sessionWatch?.check() }),

  takeAuthNotice() {
    const notice = authNotice;
    authNotice = null;
    return notice;
  },

  // A name the user actually entered outranks the account name: it is
  // stored locally the moment they join.
  getUserName: () => getPref('name') ?? userName ?? getUser()?.name,
  setUserName: (name) => {
    userName = name;
  },

  goHome() {
    setHashSilently('');
    show((r) => mountHome(r, ctx));
  },

  /** @param {{mode: 'create'|'join', code?: string}} params */
  openPrejoin(params) {
    if (params.mode === 'join' && params.code) {
      params = { ...params, code: canonicalCode(params.code) };
      setHashSilently(`/m/${encodeURIComponent(params.code)}`);
    }
    showLazy(() => import('./ui/prejoin.js'), (m, r) => m.mountPrejoin(r, params, ctx));
  },

  /**
   * "Start an instant meeting". Always through the pre-join screen: that is
   * where the host chooses the room type (Closed by default, or Open) — asked
   * every time, never remembered — and it keeps the saved name prefilled, so
   * a returning host is a single "Start meeting" click away.
   */
  startInstantMeeting() {
    ctx.openPrejoin({ mode: 'create' });
  },

  /**
   * Called by prejoin once the session is live.
   * @param {{media, session: RoomSession, roomCode: string|null, lease: RoomLease|null}} call
   *   `lease` is the room's entry in the directory, when we host one and
   *   prejoin has already taken it (see lib/rooms.js).
   */
  startCall(call) {
    activeCall = call;
    // The shareable meeting code: the persistent room when there is one,
    // otherwise the (one-time) peer invite code.
    call.meetingCode = call.roomCode ?? call.session.code;
    setHashSilently(`/m/${encodeURIComponent(call.meetingCode)}`);
    // How the room's link is doing, for the meeting info panel: fed by the
    // lease's every write (see lib/link-health.js). A meeting with no room
    // has no link to keep — the invite is the link.
    call.linkHealth = call.roomCode ? new LinkHealth() : null;
    call.linkHealth?.role({ host: call.session.isHost });
    call.disposers = [];
    if (call.lease) attachLease(call, call.lease);
    call.disposers.push(
      call.session.on('removed', () => toast('The host removed you from the meeting.', { icon: 'info', duration: 6000 })),
      call.session.on('left', onSessionEnded),
      call.session.on('ended', ({ reason }) => {
        // Nothing of the meeting is left to hold on to — the link is free.
        void releaseRoom(call);
        if (activeCall) ctx.endCall({ meetingEnded: true, reason });
      }),
      // The invite newcomers need changed: the host role moved to us, or we
      // serve under a new code. The link has to say so.
      call.session.on('code-changed', ({ code }) => onCodeChanged(call, code)),
      call.session.on('host-changed', ({ self }) => {
        if (call !== activeCall) return;
        // A host, whoever it is, writes for the room from here — holding it
        // on their behalf is done.
        stopWarming(call);
        if (self) {
          // The link is ours to keep now; the code-changed that follows,
          // once our server is up, takes the lease over.
          call.linkHealth?.role({ host: true });
          return;
        }
        // Someone else is hosting now — they carry the lease on.
        call.lease?.stop();
        call.lease = null;
        call.linkHealth?.role({ host: false });
      }),
      // The host has gone quiet. Nobody writes for the room while it has no
      // host, and the directory forgets an entry long before a successor is
      // elected — then the next person to open the link finds the code free
      // and starts a meeting of their own on it. Whoever this side would
      // elect holds the entry until a host writes again.
      call.session.on('host-unreachable', ({ keeper }) => {
        if (call !== activeCall || !call.roomCode || call.lease) return;
        if (keeper !== call.session.selfId || call.warmer) return;
        call.warmer = new RoomWarmer(call.roomCode, call.session.meetingSecret, () =>
          hostFields(call.session)
        );
        call.warmer.start();
      }),
      call.session.on('host-reachable', () => stopWarming(call)),
      // The entry carries a headcount, for whoever is looking at the link.
      call.session.on('peer-joined', () => void call.lease?.refresh()),
      call.session.on('peer-left', () => void call.lease?.refresh())
    );
    showLazy(() => import('./ui/meeting.js'), (m, r) => m.mountMeeting(r, call, ctx));
  },

  /** Leave the current call and show the post-call screen. */
  endCall({ meetingEnded = false, reason = null } = {}) {
    const call = activeCall;
    if (!call) {
      ctx.goHome();
      return;
    }
    activeCall = null;
    for (const dispose of call.disposers ?? []) dispose();
    const code = call.meetingCode ?? call.session.code;
    const wasHost = call.session.isHost;
    if (call.lease) {
      // Alone, the meeting ends with us: free the link. With people still
      // in, the successor carries the lease on — leave the entry to them.
      // So too when we lost touch with the people who were in: they may
      // be meeting under a successor now, whose entry this would end.
      if (wasHost && call.session.peerCount === 0 && !call.session.mayContinueElsewhere) void call.lease.end();
      else call.lease.stop();
      call.lease = null;
    }
    try {
      call.session.leave();
    } catch {
      /* noop */
    }
    call.media.stopAll();
    setHashSilently('');
    show((r) => mountLeft(r, { code, wasHost, meetingEnded, reason }, ctx));
    // A CRM session that ended during the call ends now that the call has.
    sessionWatch?.callEnded();
  },
};

/**
 * The meeting's live invite changed — the host role moved to us, or the
 * invite we serve was replaced under us (the library reopened its signaller
 * socket under a new code, or we re-served a dead one). Write it to the
 * room's entry so the link keeps working; for a meeting with no room the
 * invite *is* the link, so just follow it.
 */
function onCodeChanged(call, code) {
  if (call !== activeCall) return;
  if (!call.roomCode) {
    call.meetingCode = code;
    setHashSilently(`/m/${encodeURIComponent(code)}`);
    return;
  }
  if (!call.session.isHost) return;
  // The invite rotated under us (a routine signaller-socket reconnect, or a
  // dead invite we re-served). Write the new one to the link and move on —
  // no toast: nothing is wrong that the host can see or act on, and the
  // info panel's Link line already reflects the state for anyone who looks.
  call.linkHealth?.codeChanged();
  const lease = ensureLease(call);
  if (lease) void lease.refresh();
}

/**
 * The lease on the room's entry, taken (and kept) under the meeting's
 * secret — the one the first host chose and every participant was welcomed
 * with, so a successor's lease continues the first host's rather than
 * fighting it.
 */
function ensureLease(call) {
  if (!call.roomCode) return null;
  if (call.lease) return call.lease;
  const secret = call.session.meetingSecret;
  if (!secret) return null;
  const lease = new RoomLease(call.roomCode, secret, () => hostFields(call.session));
  attachLease(call, lease);
  void lease.start();
  return lease;
}

/** Hear of the lease's every write while this call is the active one. */
function attachLease(call, lease) {
  call.lease = lease;
  call.disposers.push(lease.on('beat', (beat) => onBeat(call, lease, beat)));
  // Writes it made before we listened (prejoin's claim) still count.
  if (lease.last) call.linkHealth?.beat({ ok: lease.last === 'ok', held: lease.last === 'held' });
}

function onBeat(call, lease, { ok, held, stale, room, error }) {
  if (call !== activeCall || call.lease !== lease) return;
  if (stale) {
    void onMeetingMovedOn(call, lease, room);
    return;
  }
  call.linkHealth?.beat({ ok, held });
  if (ok) {
    lastNotified.delete('link-write-failed'); // the next failure is news again
    // Only worth a word if the host was told of the trouble.
    if (call.linkAlerted) {
      call.linkAlerted = false;
      toast('Your meeting link is working again.', { icon: 'info' });
    }
    return;
  }
  if (held) {
    void onLinkHeld(call, room);
    return;
  }
  onPublishError(call, error);
}

/**
 * The directory says another meeting holds this link: someone started it in
 * the same breath as us, and their write stood. Newcomers land there. Alone,
 * we go there too — join it on the same media, and let our own meeting go.
 * With people already in ours there is nothing to move: say so, and keep
 * heartbeating; if their meeting ends first, the next beat takes the link.
 */
async function onLinkHeld(call, room) {
  if (call !== activeCall) return;
  if (call.session.peerCount === 0) {
    if (room?.status === 'live') {
      if (await moveToRival(call, room)) return;
    } else if (room?.status === 'starting') {
      // Their server is not up yet — look again in a moment.
      setTimeout(() => {
        if (call === activeCall && call.session.peerCount === 0) void call.lease?.refresh();
      }, HELD_STARTING_RECHECK_MS);
      return;
    }
  }
  notifyOnce('link-held', LINK_TROUBLE_TOAST_EVERY_MS, () => {
    call.linkAlerted = true;
    toast(
      'Someone else started a meeting on this link at the same time. New participants will join theirs; this meeting carries on for everyone in it.',
      { icon: 'info' }
    );
  });
}

const HELD_STARTING_RECHECK_MS = 3000;
const LINK_TROUBLE_TOAST_EVERY_MS = 5 * 60_000;
const lastNotified = new Map();
/** Say something at most once per `everyMs` — a flapping link must not flood the call with toasts. */
function notifyOnce(key, everyMs, show) {
  const last = lastNotified.get(key) ?? -Infinity;
  if (Date.now() - last < everyMs) return;
  lastNotified.set(key, Date.now());
  show();
}

/** A heartbeat did not land: the entry is ageing towards gone while the meeting hums along. */
function onPublishError(call, error) {
  console.warn('[app] could not write the meeting link', error);
  if (call !== activeCall) return;
  notifyOnce('link-write-failed', LINK_TROUBLE_TOAST_EVERY_MS, () => {
    call.linkAlerted = true;
    toast('The meeting link couldn’t be updated just now. New participants may not find this meeting until it can be.', {
      icon: 'info',
    });
  });
}

/** The meeting is over (we were the last one in it): free the link. */
/** Stop holding a room entry on an absent host's behalf. */
function stopWarming(call) {
  call.warmer?.stop();
  call.warmer = null;
}

function releaseRoom(call) {
  stopWarming(call);
  if (!call.roomCode) return Promise.resolve(false);
  if (call.lease) {
    const lease = call.lease;
    call.lease = null;
    return lease.end();
  }
  return endRoom(call.roomCode, call.session.meetingSecret, call.session.term);
}

/**
 * The directory turned our heartbeat away as coming from a term the meeting
 * has left behind: while we were out of reach the others elected a
 * successor, and it is writing for the room now. We are not this meeting's
 * host any more, whatever our session still thinks, and must never write
 * for the room again — nor release it, which would end the successor's
 * meeting. With nobody left on a link of ours, go to where the meeting is;
 * with someone still here, we are a piece of it that split off, and say so.
 */
async function onMeetingMovedOn(call, lease, room) {
  if (call !== activeCall || call.lease !== lease) return;
  lease.stop();
  call.lease = null;
  call.linkHealth?.role({ host: false });
  console.warn('[app] the meeting moved on to a later host while we were out of reach');
  if (call.session.connectedPeerIds().length > 0) {
    notifyOnce('meeting-moved-on', LINK_TROUBLE_TOAST_EVERY_MS, () =>
      toast('The meeting continued under a new host while you were disconnected. New participants will join them.', {
        icon: 'info',
      })
    );
    return;
  }
  if (room?.status !== 'live' || !room.invite) return;
  await moveToMeeting(call, room, {
    alone: () => call.session.connectedPeerIds().length === 0,
    message: 'The meeting continued under a new host while you were disconnected — you’ve rejoined it.',
  });
}

/**
 * We started this room's meeting, but someone else started it in the same
 * breath and the directory took theirs — theirs is the one newcomers will
 * find, so, being alone, we go there too: join it on the same media, then
 * let our own meeting go. The call carries on in place; only the session
 * underneath changes. Resolves whether we moved.
 */
async function moveToRival(call, room) {
  return moveToMeeting(call, room, {
    alone: () => call.session.peerCount === 0,
    message: 'Someone else started this meeting at the same time — you’ve joined them.',
  });
}

/**
 * Carry the call over to the meeting at `room.invite`: join it on the same
 * media, then let our own session go. The call carries on in place; only
 * the session underneath changes. `alone()` is asked again once the join is
 * in, since a meeting someone joined meanwhile is not ours to walk out of.
 * Resolves whether we moved.
 */
async function moveToMeeting(call, room, { alone, message }) {
  if (call !== activeCall) return false;
  const session = new RoomSession({ media: call.media, name: call.session.name, staff: isSignedIn() });
  try {
    await session.join(room.invite);
  } catch (error) {
    leaveQuietly(session);
    console.warn('[app] could not join the meeting at', room.invite, error);
    return false;
  }
  if (call !== activeCall || !alone()) {
    // The call ended meanwhile — or someone joined us, and a meeting with
    // people in it is the meeting: it stays.
    leaveQuietly(session);
    return false;
  }
  for (const dispose of call.disposers ?? []) dispose();
  call.lease?.stop(); // the entry is theirs, not ours — nothing to release
  call.lease = null;
  leaveQuietly(call.session);
  toast(message, { icon: 'info' });
  ctx.startCall({ media: call.media, session, roomCode: call.roomCode, lease: null });
  return true;
}

function leaveQuietly(session) {
  try {
    session.leave();
  } catch {
    /* noop */
  }
}

function onSessionEnded() {
  // Session ended underneath us (e.g. connection loss) while in a call.
  if (activeCall) ctx.endCall();
}

/** Bumped on every view change, so a view still loading when another is asked for is dropped. */
let viewRun = 0;

function show(mount) {
  viewRun++;
  unmount?.();
  unmount = mount(root) ?? null;
}

/**
 * Show a view whose code is loaded on demand (the green room and the meeting are not
 * needed to paint the home screen). If another view is asked for while it loads, it never shows.
 */
function showLazy(load, mount) {
  const run = ++viewRun;
  void load().then((module) => {
    if (run !== viewRun) return;
    show((r) => mount(module, r));
  });
}

/** Update the hash without triggering our own hashchange handling. */
let suppressHash = false;
function setHashSilently(hash) {
  const target = hash ? `#${hash}` : '';
  const current = location.hash === '#' ? '' : location.hash;
  if (current === (target || '')) return;
  suppressHash = true;
  if (target) location.hash = target;
  else history.replaceState(null, '', location.pathname + location.search);
  setTimeout(() => {
    suppressHash = false;
  }, 0);
}

/**
 * Canonical form of a meeting code: room codes get normalized ("ABC DEFG
 * HIJ" → "abc-defg-hij") so history entries, the active call, and typed
 * input all compare equal; legacy invite codes pass through untouched.
 */
function canonicalCode(code) {
  return normalizeRoomCode(code) ?? code;
}

function route() {
  if (suppressHash) return;
  const r = parseHash(location.hash);
  if (r.view === 'meeting' && r.code) {
    const code = canonicalCode(r.code);
    if (activeCall && (activeCall.meetingCode ?? activeCall.session.code) === code) return; // already in it
    if (activeCall) ctx.endCall();
    ctx.openPrejoin({ mode: 'join', code });
  } else {
    if (activeCall) {
      // Back button out of a meeting = leave.
      ctx.endCall();
      return;
    }
    ctx.goHome();
  }
}

/** Post-call screen. */
function mountLeft(rootEl, { code, wasHost, meetingEnded, reason }, appCtx) {
  clear(rootEl);
  let seconds = 60;
  const countdown = el('span', {}, `Returning to home screen in ${seconds}s`);
  const timer = setInterval(() => {
    seconds -= 1;
    countdown.textContent = `Returning to home screen in ${seconds}s`;
    if (seconds <= 0) appCtx.goHome();
  }, 1000);

  const title = meetingEnded ? 'The meeting has ended' : 'You left the meeting';
  // Leaving removes the button that was focused, which drops focus to
  // <body> — so the keyboard restarts from the top of the document and a
  // screen reader is told nothing at all about where it landed. Give the
  // heading focus instead: it names the new screen, and the controls
  // follow it.
  const heading = el('h1', { style: { fontWeight: '400', margin: '0' }, tabindex: '-1' }, title);
  // A persistent room outlives the session: rejoining works, and anyone
  // holding the link can start it again when nothing is running. A legacy
  // code is one host's dead peer server, so there is nothing to go back to.
  const roomful = isRoomCode(code);

  rootEl.append(
    el(
      'div',
      { class: 'home' },
      el('header', { class: 'home-header' }, brandLockup()),
      el(
        'main',
        { class: 'home-main', style: { flexDirection: 'column', gap: '20px', textAlign: 'center' } },
        heading,
        meetingEnded
          ? el(
              'p',
              { style: { color: 'var(--text-secondary)', margin: '0' } },
              // The session's own reason (e.g. a takeover that could not
              // serve) beats the generic story, which would be wrong there.
              (reason ?? 'Everyone has left, so the meeting is over.') +
                (roomful ? ' The link still works, so anyone holding it can start the meeting again.' : '')
            )
          : null,
        el('p', { style: { color: 'var(--text-secondary)', margin: '0' } }, countdown),
        el(
          'div',
          { style: { display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' } },
          roomful
            ? el(
                'button',
                { class: 'btn btn-outline', onclick: () => appCtx.openPrejoin({ mode: 'join', code }) },
                icon('videoCall'),
                wasHost ? 'Restart meeting' : 'Rejoin'
              )
            : wasHost || meetingEnded
              ? el(
                  'button',
                  {
                    class: 'btn btn-outline',
                    onclick: async (e) => {
                      // Their choice is made: the countdown must not pull
                      // the screen away under a start in progress.
                      clearInterval(timer);
                      const btn = e.currentTarget;
                      btn.disabled = true;
                      await appCtx.startInstantMeeting();
                      btn.disabled = false;
                    },
                  },
                  icon('videoCall'),
                  'Start a new meeting'
                )
              : code
                ? el(
                    'button',
                    { class: 'btn btn-outline', onclick: () => appCtx.openPrejoin({ mode: 'join', code }) },
                    icon('videoCall'),
                    'Rejoin'
                  )
                : null,
          el('button', { class: 'btn btn-primary', onclick: () => appCtx.goHome() }, 'Return to home screen')
        )
      )
    )
  );

  heading.focus();

  return () => clearInterval(timer);
}

// Leave gracefully when the tab closes. A lone host frees the link with a
// beacon, which the browser sends after the page is gone; with people still
// in, the successor's lease takes over — and if nothing of ours lands, the
// entry simply ages out. `pagehide` as well as `beforeunload`: iOS Safari
// does not fire the latter, and a page going into the back/forward cache
// skips it elsewhere too — a phone host swiping the tab away would
// otherwise say no goodbye, and the others would wait out the reconnect
// deadline on a ghost. Both handlers may run; everything here is idempotent.
const leaveOnUnload = () => {
  const call = activeCall;
  if (!call) return;
  try {
    if (call.lease && call.session.isHost && call.session.peerCount === 0 && !call.session.mayContinueElsewhere) {
      call.lease.endOnUnload();
    }
    else call.lease?.stop();
    call.session.leave();
  } catch {
    /* noop */
  }
};
window.addEventListener('beforeunload', leaveOnUnload);
window.addEventListener('pagehide', leaveOnUnload);

window.addEventListener('hashchange', route);

// Boot: a CRM sign-in that did not complete says why, the server says who is signed in, a link
// whose path is a room id (https://meet.example.com/TRT-abc123-def456) becomes the in-app route, then route.
const bootAuthError = takeAuthError();
if (bootAuthError) authNotice = bootAuthError;
applyLanguage(detectLanguage());
await initAuth();
const roomInPath = roomFromPath(location.pathname);
if (roomInPath) history.replaceState(null, '', `/${location.search}#/m/${encodeURIComponent(roomInPath)}`);
sessionWatch = startSessionWatch({
  inCall: () => !!activeCall,
  onWarning: () =>
    toast('Your trtCRM session has ended. You can finish this call; you will be signed out when it ends.', { icon: 'info' }),
  onEnded: (status) => {
    authNotice = status;
    // Not in the middle of anything: show the sign-in page. (The post-call screen goes home by itself.)
    if (!activeCall && root.querySelector('.home')) ctx.goHome();
  },
});
route();
